import { z } from 'zod';
import * as A from './api.js';
import * as P from './projects.js';
import * as V from './advisors.js';
import * as S from './scores.js';
import * as L from './alerts.js';
import type { Role } from './enums.js';

// Single source of truth for the REST surface under /api/v1.
// The API validates requests with these schemas, the web client derives its types from them,
// and the OpenAPI document is generated from this registry.

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface RouteDef<
  P extends z.ZodType = z.ZodType,
  Q extends z.ZodType = z.ZodType,
  B extends z.ZodType = z.ZodType,
  R extends z.ZodType = z.ZodType,
> {
  method: HttpMethod;
  /** Express-style path relative to /api/v1, e.g. /tenants/:tenantId/agents */
  path: string;
  summary: string;
  tag: string;
  /** Minimum tenant role. Undefined for non-tenant routes. */
  minRole?: Role;
  /** No session required (authorization comes from a capability such as a pairing token). */
  public?: boolean;
  /** Internal Estateflow route: requires User.isPlatformAdmin (no tenant data beyond aggregates). */
  platformAdmin?: boolean;
  params?: P;
  query?: Q;
  body?: B;
  response: R;
  /** Non-JSON responses (CSV, media, SSE). */
  produces?: 'application/json' | 'text/csv' | 'text/event-stream' | 'application/octet-stream';
}

function route<P extends z.ZodType, Q extends z.ZodType, B extends z.ZodType, R extends z.ZodType>(
  def: RouteDef<P, Q, B, R>,
): RouteDef<P, Q, B, R> {
  return def;
}

const TenantParams = z.object({ tenantId: z.string().min(1) });
const T = <S extends z.ZodRawShape>(shape: S) => TenantParams.extend(shape);
const IdParam = z.string().min(1).max(64);

export const routes = {
  // Session / tenancy
  me: route({ method: 'GET', path: '/me', summary: 'Usuario actual y membresías', tag: 'session', response: A.MeResponseSchema }),

  // Settings
  getSettings: route({
    method: 'GET', path: '/tenants/:tenantId/settings', summary: 'Configuración del negocio', tag: 'settings',
    minRole: 'viewer', params: TenantParams, response: A.TenantSettingsSchema,
  }),
  updateSettings: route({
    method: 'PATCH', path: '/tenants/:tenantId/settings', summary: 'Actualizar configuración', tag: 'settings',
    minRole: 'owner', params: TenantParams, body: A.UpdateTenantSettingsSchema, response: A.TenantSettingsSchema,
  }),
  createHoliday: route({
    method: 'POST', path: '/tenants/:tenantId/holidays', summary: 'Agregar día inhábil', tag: 'settings',
    minRole: 'owner', params: TenantParams, body: A.CreateHolidaySchema, response: A.TenantSettingsSchema,
  }),
  deleteHoliday: route({
    method: 'DELETE', path: '/tenants/:tenantId/holidays/:holidayId', summary: 'Eliminar día inhábil', tag: 'settings',
    minRole: 'owner', params: T({ holidayId: IdParam }), response: A.TenantSettingsSchema,
  }),

  // Projects (desarrollos)
  listProjects: route({
    method: 'GET', path: '/tenants/:tenantId/projects', summary: 'Proyectos (desarrollos)', tag: 'projects',
    minRole: 'viewer', params: TenantParams, response: z.object({ items: z.array(P.ProjectSummarySchema) }),
  }),
  getProject: route({
    method: 'GET', path: '/tenants/:tenantId/projects/:projectId', summary: 'Detalle de proyecto', tag: 'projects',
    minRole: 'viewer', params: T({ projectId: IdParam }), response: P.ProjectSchema,
  }),
  createProject: route({
    method: 'POST', path: '/tenants/:tenantId/projects', summary: 'Crear proyecto', tag: 'projects',
    minRole: 'manager', params: TenantParams, body: P.CreateProjectSchema, response: P.ProjectSchema,
  }),
  updateProject: route({
    method: 'PATCH', path: '/tenants/:tenantId/projects/:projectId', summary: 'Editar o archivar proyecto', tag: 'projects',
    minRole: 'manager', params: T({ projectId: IdParam }), body: P.UpdateProjectSchema, response: P.ProjectSchema,
  }),
  linkConversationProject: route({
    method: 'POST', path: '/tenants/:tenantId/conversations/:conversationId/project', summary: 'Vincular conversación a un proyecto', tag: 'projects',
    minRole: 'manager', params: T({ conversationId: IdParam }), body: P.LinkConversationProjectSchema, response: A.OkSchema,
  }),

  // Agents
  listAgents: route({
    method: 'GET', path: '/tenants/:tenantId/agents', summary: 'Directorio de agentes', tag: 'agents',
    minRole: 'viewer', params: TenantParams, response: z.object({ items: z.array(A.AgentSchema) }),
  }),
  createAgent: route({
    method: 'POST', path: '/tenants/:tenantId/agents', summary: 'Crear agente', tag: 'agents',
    minRole: 'manager', params: TenantParams, body: A.CreateAgentSchema, response: A.AgentSchema,
  }),
  updateAgent: route({
    method: 'PATCH', path: '/tenants/:tenantId/agents/:agentId', summary: 'Editar agente', tag: 'agents',
    minRole: 'manager', params: T({ agentId: IdParam }), body: A.UpdateAgentSchema, response: A.AgentSchema,
  }),

  // Members
  listMembers: route({
    method: 'GET', path: '/tenants/:tenantId/members', summary: 'Usuarios y roles', tag: 'members',
    minRole: 'owner', params: TenantParams, response: z.object({ items: z.array(A.MemberSchema) }),
  }),
  createMember: route({
    method: 'POST', path: '/tenants/:tenantId/members', summary: 'Dar acceso a un usuario', tag: 'members',
    minRole: 'owner', params: TenantParams, body: A.CreateMemberSchema, response: A.MemberSchema,
  }),
  updateMember: route({
    method: 'PATCH', path: '/tenants/:tenantId/members/:memberId', summary: 'Cambiar rol', tag: 'members',
    minRole: 'owner', params: T({ memberId: IdParam }), body: A.UpdateMemberSchema, response: A.MemberSchema,
  }),
  deleteMember: route({
    method: 'DELETE', path: '/tenants/:tenantId/members/:memberId', summary: 'Revocar acceso', tag: 'members',
    minRole: 'owner', params: T({ memberId: IdParam }), response: A.OkSchema,
  }),

  // Connections
  listConnections: route({
    method: 'GET', path: '/tenants/:tenantId/connections', summary: 'Números conectados', tag: 'connections',
    minRole: 'viewer', params: TenantParams, response: z.object({ items: z.array(A.ConnectionSchema) }),
  }),
  getConnection: route({
    method: 'GET', path: '/tenants/:tenantId/connections/:connectionId', summary: 'Detalle de número', tag: 'connections',
    minRole: 'viewer', params: T({ connectionId: IdParam }), response: A.ConnectionDetailSchema,
  }),
  createConnection: route({
    method: 'POST', path: '/tenants/:tenantId/connections', summary: 'Agregar número (inicia emparejamiento QR)', tag: 'connections',
    minRole: 'manager', params: TenantParams, body: A.CreateConnectionSchema, response: A.ConnectionSchema,
  }),
  updateConnection: route({
    method: 'PATCH', path: '/tenants/:tenantId/connections/:connectionId', summary: 'Renombrar número', tag: 'connections',
    minRole: 'manager', params: T({ connectionId: IdParam }), body: A.UpdateConnectionSchema, response: A.ConnectionSchema,
  }),
  assignConnection: route({
    method: 'POST', path: '/tenants/:tenantId/connections/:connectionId/assignment', summary: 'Asignar agente o marcar compartido', tag: 'connections',
    minRole: 'manager', params: T({ connectionId: IdParam }), body: A.AssignConnectionSchema, response: A.ConnectionSchema,
  }),
  pauseConnection: route({
    method: 'POST', path: '/tenants/:tenantId/connections/:connectionId/pause', summary: 'Pausar captura (no cierra sesión)', tag: 'connections',
    minRole: 'manager', params: T({ connectionId: IdParam }), response: A.ConnectionSchema,
  }),
  resumeConnection: route({
    method: 'POST', path: '/tenants/:tenantId/connections/:connectionId/resume', summary: 'Reanudar captura', tag: 'connections',
    minRole: 'manager', params: T({ connectionId: IdParam }), response: A.ConnectionSchema,
  }),
  reconnectConnection: route({
    method: 'POST', path: '/tenants/:tenantId/connections/:connectionId/reconnect', summary: 'Reconectar o volver a emparejar', tag: 'connections',
    minRole: 'manager', params: T({ connectionId: IdParam }), response: A.ConnectionSchema,
  }),
  unlinkConnection: route({
    method: 'POST', path: '/tenants/:tenantId/connections/:connectionId/unlink', summary: 'Desvincular dispositivo (revoca la sesión)', tag: 'connections',
    minRole: 'manager', params: T({ connectionId: IdParam }), body: A.UnlinkConnectionSchema, response: A.ConnectionSchema,
  }),

  deleteConnection: route({
    method: 'DELETE', path: '/tenants/:tenantId/connections/:connectionId', summary: 'Eliminar un número no activo (pausado o desvinculado) y todos sus datos', tag: 'connections',
    minRole: 'owner', params: T({ connectionId: IdParam }), body: A.DeleteConnectionSchema, response: A.OkSchema,
  }),

  createPairingLink: route({
    method: 'POST', path: '/tenants/:tenantId/connections/:connectionId/pairing-link', summary: 'Crear enlace de vinculación para el asesor (revoca el anterior)', tag: 'connections',
    minRole: 'manager', params: T({ connectionId: IdParam }), body: A.CreatePairingLinkSchema, response: A.CreatedPairingLinkSchema,
  }),
  revokePairingLink: route({
    method: 'DELETE', path: '/tenants/:tenantId/connections/:connectionId/pairing-link', summary: 'Revocar enlace de vinculación', tag: 'connections',
    minRole: 'manager', params: T({ connectionId: IdParam }), response: A.OkSchema,
  }),

  // History backfill (recover older history for a connected number)
  startHistoryBackfill: route({
    method: 'POST', path: '/tenants/:tenantId/connections/:connectionId/history-backfill', summary: 'Recuperar historial anterior (hasta la retención)', tag: 'connections',
    minRole: 'manager', params: T({ connectionId: IdParam }), body: A.StartHistoryBackfillSchema, response: A.HistoryBackfillSchema,
  }),
  cancelHistoryBackfill: route({
    method: 'DELETE', path: '/tenants/:tenantId/connections/:connectionId/history-backfill', summary: 'Cancelar la recuperación de historial', tag: 'connections',
    minRole: 'manager', params: T({ connectionId: IdParam }), response: A.OkSchema,
  }),

  // Self-service pairing (public; token in the X-Pairing-Token header, never in the URL)
  pairingStatus: route({
    method: 'GET', path: '/pairing', summary: 'Estado del enlace de vinculación y QR vigente', tag: 'pairing',
    public: true, response: A.PairingStatusSchema,
  }),
  startPairing: route({
    method: 'POST', path: '/pairing/start', summary: 'Solicitar un código QR nuevo desde el enlace', tag: 'pairing',
    public: true, response: A.PairingStatusSchema,
  }),

  // Conversations
  listConversations: route({
    method: 'GET', path: '/tenants/:tenantId/conversations', summary: 'Conversaciones', tag: 'conversations',
    minRole: 'viewer', params: TenantParams, query: A.ConversationListQuerySchema, response: A.ConversationPageSchema,
  }),
  getConversation: route({
    method: 'GET', path: '/tenants/:tenantId/conversations/:conversationId', summary: 'Detalle, episodios, métricas y análisis', tag: 'conversations',
    minRole: 'viewer', params: T({ conversationId: IdParam }), response: A.ConversationDetailSchema,
  }),
  listMessages: route({
    method: 'GET', path: '/tenants/:tenantId/conversations/:conversationId/messages', summary: 'Transcripción de solo lectura', tag: 'conversations',
    minRole: 'viewer', params: T({ conversationId: IdParam }), query: A.MessageListQuerySchema, response: A.MessagePageSchema,
  }),
  excludeConversation: route({
    method: 'POST', path: '/tenants/:tenantId/conversations/:conversationId/exclude', summary: 'Excluir de reportes e IA', tag: 'conversations',
    minRole: 'manager', params: T({ conversationId: IdParam }), body: A.ExcludeConversationSchema, response: A.OkSchema,
  }),
  restoreConversation: route({
    method: 'POST', path: '/tenants/:tenantId/conversations/:conversationId/restore', summary: 'Restaurar elegibilidad', tag: 'conversations',
    minRole: 'manager', params: T({ conversationId: IdParam }), response: A.OkSchema,
  }),
  closeEpisode: route({
    method: 'POST', path: '/tenants/:tenantId/conversations/:conversationId/close-episode', summary: 'Cerrar episodio actual', tag: 'conversations',
    minRole: 'manager', params: T({ conversationId: IdParam }), response: A.OkSchema,
  }),
  reanalyzeEpisode: route({
    method: 'POST', path: '/tenants/:tenantId/conversations/:conversationId/episodes/:episodeId/reanalyze', summary: 'Solicitar nuevo análisis (limitado)', tag: 'conversations',
    minRole: 'manager', params: T({ conversationId: IdParam, episodeId: IdParam }), response: A.OkSchema,
  }),
  getMedia: route({
    method: 'GET', path: '/tenants/:tenantId/media/:mediaId', summary: 'Contenido multimedia privado', tag: 'conversations',
    minRole: 'viewer', params: T({ mediaId: IdParam }), response: z.unknown(), produces: 'application/octet-stream',
  }),

  // Reports
  overview: route({
    method: 'GET', path: '/tenants/:tenantId/reports/overview', summary: 'Resumen', tag: 'reports',
    minRole: 'viewer', params: TenantParams, query: A.ReportFiltersSchema, response: A.OverviewReportSchema,
  }),
  performance: route({
    method: 'GET', path: '/tenants/:tenantId/reports/performance', summary: 'Desempeño por número o agente asignado', tag: 'reports',
    minRole: 'viewer', params: TenantParams, query: A.PerformanceQuerySchema, response: A.PerformanceReportSchema,
  }),
  advisorReport: route({
    method: 'GET', path: '/tenants/:tenantId/reports/advisors', summary: 'Tablero de asesores (criterios objetivos)', tag: 'reports',
    minRole: 'viewer', params: TenantParams, query: V.AdvisorReportQuerySchema, response: V.AdvisorReportSchema,
  }),
  criterionDrilldown: route({
    method: 'GET', path: '/tenants/:tenantId/reports/advisors/criteria', summary: 'Conversaciones por criterio (drill-down)', tag: 'reports',
    minRole: 'viewer', params: TenantParams, query: V.CriterionDrilldownQuerySchema, response: V.CriterionDrilldownSchema,
  }),
  calibrationSummary: route({
    method: 'GET', path: '/tenants/:tenantId/calibration', summary: 'Estado de calibración de los puntajes', tag: 'reports',
    minRole: 'manager', params: TenantParams, response: S.CalibrationSummarySchema,
  }),
  saveCalibrationReview: route({
    method: 'PUT', path: '/tenants/:tenantId/conversations/:conversationId/episodes/:episodeId/calibration', summary: 'Guardar revisión manual para calibración', tag: 'reports',
    minRole: 'manager', params: T({ conversationId: IdParam, episodeId: IdParam }), body: S.CalibrationReviewInputSchema, response: S.CalibrationReviewSchema,
  }),
  exportCsv: route({
    method: 'GET', path: '/tenants/:tenantId/reports/export', summary: 'Exportar CSV', tag: 'reports',
    minRole: 'manager', params: TenantParams, query: A.ExportQuerySchema, response: z.string(), produces: 'text/csv',
  }),

  aiRetrofitStatus: route({
    method: 'GET', path: '/tenants/:tenantId/ai/retrofit', summary: 'Conversaciones pendientes de evaluar con el prompt actual', tag: 'settings',
    minRole: 'owner', params: TenantParams, query: A.AiRetrofitQuerySchema, response: A.AiRetrofitStatusSchema,
  }),
  startAiRetrofit: route({
    method: 'POST', path: '/tenants/:tenantId/ai/retrofit', summary: 'Reanalizar con el prompt actual (en segundo plano)', tag: 'settings',
    minRole: 'owner', params: TenantParams, body: A.StartAiRetrofitSchema, response: A.StartedAiRetrofitSchema,
  }),

  // Alerts (in-app)
  listAlerts: route({
    method: 'GET', path: '/tenants/:tenantId/alerts', summary: 'Bandeja de alertas', tag: 'alerts',
    minRole: 'viewer', params: TenantParams, query: L.AlertListQuerySchema, response: L.AlertPageSchema,
  }),
  alertSummary: route({
    method: 'GET', path: '/tenants/:tenantId/alerts/summary', summary: 'Conteo de alertas activas', tag: 'alerts',
    minRole: 'viewer', params: TenantParams, response: L.AlertSummarySchema,
  }),
  getAlert: route({
    method: 'GET', path: '/tenants/:tenantId/alerts/:alertId', summary: 'Detalle de alerta con bitácora', tag: 'alerts',
    minRole: 'viewer', params: T({ alertId: IdParam }), response: L.AlertDetailSchema,
  }),
  alertAction: route({
    method: 'POST', path: '/tenants/:tenantId/alerts/:alertId/actions', summary: 'Marcar vista, posponer o resolver', tag: 'alerts',
    minRole: 'manager', params: T({ alertId: IdParam }), body: L.AlertActionSchema, response: L.AlertDetailSchema,
  }),

  // Operations
  processingHealth: route({
    method: 'GET', path: '/tenants/:tenantId/health', summary: 'Salud de conexiones y procesamiento', tag: 'operations',
    minRole: 'viewer', params: TenantParams, response: A.ProcessingHealthSchema,
  }),
  audit: route({
    method: 'GET', path: '/tenants/:tenantId/audit', summary: 'Bitácora de auditoría', tag: 'operations',
    minRole: 'owner', params: TenantParams, query: z.object({ cursor: z.string().optional(), limit: z.coerce.number().int().min(1).max(100).default(50) }),
    response: A.AuditPageSchema,
  }),
  adminAiUsage: route({
    method: 'GET', path: '/admin/ai-usage', summary: 'Uso de IA por negocio y mes (interno)', tag: 'admin',
    platformAdmin: true, query: A.AiUsageQuerySchema, response: A.AiUsageReportSchema,
  }),
  events: route({
    method: 'GET', path: '/tenants/:tenantId/events', summary: 'Eventos en tiempo real (SSE)', tag: 'operations',
    minRole: 'viewer', params: TenantParams, response: z.unknown(), produces: 'text/event-stream',
  }),
} as const;

export type Routes = typeof routes;
export type RouteName = keyof Routes;
export type RouteResponse<N extends RouteName> = z.infer<Routes[N]['response']>;
export type RouteBody<N extends RouteName> = Routes[N] extends { body: infer B extends z.ZodType } ? z.input<B> : never;
export type RouteQuery<N extends RouteName> = Routes[N] extends { query: infer Q extends z.ZodType } ? z.input<Q> : never;
export type RouteParams<N extends RouteName> = Routes[N] extends { params: infer P extends z.ZodType } ? z.input<P> : never;

export const API_PREFIX = '/api/v1';

/** Fill :params into a route path. */
export function buildPath(path: string, params: Record<string, string> = {}): string {
  return path.replace(/:([A-Za-z]+)/g, (_, k: string) => {
    const v = params[k];
    if (v === undefined) throw new Error(`Missing path param ${k}`);
    return encodeURIComponent(v);
  });
}

/** Minimal OpenAPI 3.1 document generated from the registry. */
export function buildOpenApiDocument(): Record<string, unknown> {
  const paths: Record<string, Record<string, unknown>> = {};
  for (const [name, r] of Object.entries(routes) as [string, RouteDef][]) {
    const oaPath = API_PREFIX + r.path.replace(/:([A-Za-z]+)/g, '{$1}');
    const parameters: unknown[] = [];
    for (const m of r.path.matchAll(/:([A-Za-z]+)/g)) {
      parameters.push({ name: m[1], in: 'path', required: true, schema: { type: 'string' } });
    }
    if (r.query) {
      const js = z.toJSONSchema(r.query, { io: 'input', unrepresentable: 'any' }) as {
        properties?: Record<string, unknown>;
        required?: string[];
      };
      for (const [k, s] of Object.entries(js.properties ?? {})) {
        parameters.push({ name: k, in: 'query', required: js.required?.includes(k) ?? false, schema: s });
      }
    }
    const op: Record<string, unknown> = {
      operationId: name,
      summary: r.summary,
      tags: [r.tag],
      parameters,
      'x-min-role': r.minRole,
      ...(r.public ? { security: [], parameters: [...parameters, { name: 'X-Pairing-Token', in: 'header', required: true, schema: { type: 'string' } }] } : {}),
      responses: {
        '200': {
          description: 'OK',
          content: {
            [r.produces ?? 'application/json']: {
              schema: r.produces && r.produces !== 'application/json' ? { type: 'string' } : z.toJSONSchema(r.response, { unrepresentable: 'any' }),
            },
          },
        },
        default: { description: 'Error', content: { 'application/json': { schema: z.toJSONSchema(A.ErrorResponseSchema) } } },
      },
    };
    if (r.body) {
      op['requestBody'] = {
        required: true,
        content: { 'application/json': { schema: z.toJSONSchema(r.body, { io: 'input', unrepresentable: 'any' }) } },
      };
    }
    paths[oaPath] = { ...(paths[oaPath] ?? {}), [r.method.toLowerCase()]: op };
  }
  return {
    openapi: '3.1.0',
    info: {
      title: 'Convometry API',
      version: '1.0.0',
      description:
        'Convometry by Estateflow — Analítica de conversaciones de venta por WhatsApp. Autenticación por cookie de sesión HttpOnly (Better Auth en /api/auth).',
    },
    servers: [{ url: '/' }],
    paths,
  };
}
